/**
 * Client-side document text extraction.
 * All libraries are lazy-loaded via dynamic import() — zero cost until a user
 * actually attaches a file of that type.
 */

export interface ExtractionResult {
  ok: boolean
  fileName: string
  fileType: string
  text: string
  error?: string
  visionFiles?: File[]
}

const SUPPORTED_EXTENSIONS: Record<string, string> = {
  pdf: 'pdf',
  xlsx: 'xlsx',
  xls: 'xlsx',
  docx: 'docx',
}

function getExtension(file: File): string {
  return (file.name.split('.').pop() || '').toLowerCase()
}

function hasUsefulExtractedText(text: string): boolean {
  const normalized = text.replace(/\s+/g, ' ').trim()
  if (normalized.length < 120) return false
  const words = normalized.match(/[A-Za-z0-9$.,#/-]{2,}/g) || []
  return words.length >= 15
}

export function isExtractableDocument(file: File): boolean {
  const ext = getExtension(file)
  return ext in SUPPORTED_EXTENSIONS
}

export async function extractDocumentText(file: File): Promise<ExtractionResult> {
  const ext = getExtension(file)
  const fileType = SUPPORTED_EXTENSIONS[ext]

  if (!fileType) {
    return {
      ok: false,
      fileName: file.name,
      fileType: ext,
      text: '',
      error: `Unsupported file type: .${ext}`,
    }
  }

  try {
    let text: string

    if (fileType === 'pdf') {
      const { extractPdfText, renderPdfPagesAsImages } = await import('./pdf')
      text = await extractPdfText(file)

      if (!hasUsefulExtractedText(text)) {
        const visionFiles = await renderPdfPagesAsImages(file)
        if (visionFiles.length > 0) {
          return {
            ok: false,
            fileName: file.name,
            fileType,
            text: '',
            error: `No embedded PDF text found; rendered ${visionFiles.length} page image(s) for AI Vision OCR.`,
            visionFiles,
          }
        }
      }
    } else if (fileType === 'xlsx') {
      const { extractXlsxText } = await import('./xlsx')
      text = await extractXlsxText(file)
    } else if (fileType === 'docx') {
      const { extractDocxText } = await import('./docx')
      text = await extractDocxText(file)
    } else {
      return {
        ok: false,
        fileName: file.name,
        fileType,
        text: '',
        error: `No extractor for type: ${fileType}`,
      }
    }

    if (!text.trim()) {
      return {
        ok: false,
        fileName: file.name,
        fileType,
        text: '',
        error: 'No text could be extracted. The file may be image-based or empty.',
      }
    }

    return { ok: true, fileName: file.name, fileType, text }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    console.error(`[DocExtract] Failed to extract ${file.name}:`, err)
    return {
      ok: false,
      fileName: file.name,
      fileType,
      text: '',
      error: `Extraction failed: ${msg}`,
    }
  }
}
